import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { API, getAuth, persistAuth, fetchProfileAndCompletionStatus, clearAuth } from '../api'; 
import './Landing.css';
import './RegisterPage.css';
import LegalModal from './LegalModal';
import {
  calculateAgeFromDateOfBirth,
  TRAINING_BACKGROUND_OPTIONS,
  loadProfileExtras,
  saveProfileExtras,
  splitDateOfBirth,
} from '../utils/profileExtras';
import { HelpCircleIcon } from './Icons';

// Must match backend @Pattern in UserRegisterDto
const PASSWORD_REGEX = /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[.@#$%^&+=*!])(?=\S+$).{8,64}$/;

const GENDER_OPTIONS = ['Male', 'Female', 'Non-binary', 'Other', 'Prefer not to say'];

const parseSleepGoal = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;

  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) return numericValue;

  if (typeof value === 'string') {
    const hourMatch = value.match(/(\d+)\s*h/i);
    const minuteMatch = value.match(/(\d+)\s*m/i);
    const hours = hourMatch ? Number(hourMatch[1]) : 0;
    const minutes = minuteMatch ? Number(minuteMatch[1]) : 0;
    if (hours || minutes) return hours * 60 + minutes;
  }

  return null;
};

// Sleep goal: stored as integer minutes for the backend; labels stay UI-only.
const SLEEP_GOAL_OPTIONS = (() => {
  const out = [];
  for (let h = 5; h <= 11; h++) {
    out.push({ label: `${h}h`, value: h * 60 });
    if (h < 11) out.push({ label: `${h}h 30m`, value: h * 60 + 30 });
  }
  return out;
})();

// Replaces the old ACTIVITY_LEVEL_OPTIONS. Two separate fields on the
// backend: typicalDay is a descriptive string, trainingBackground is an
// integer bucket (10/20/30/40/50/60).
const TYPICAL_DAY_OPTIONS = ['Mostly Sitting', 'Mostly Standing', 'Mostly Moving'];

// ─── ICONS ───

function LogoMark() {
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
      <circle cx="28" cy="28" r="28" fill="rgba(92,184,154,0.12)" />
      <circle cx="28" cy="28" r="19" fill="rgba(92,184,154,0.22)" />
      <path d="M19 28H23L26 21L30 35L34 25L36 28H39" stroke="#5CB89A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#A0ABA8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M22 6L12 13L2 6" stroke="#A0ABA8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M19 11H5C3.9 11 3 11.9 3 13V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V13C21 11.9 20.1 11 19 11Z" stroke="#A0ABA8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7 11V7C7 5.67 7.53 4.4 8.46 3.46C9.4 2.53 10.67 2 12 2C13.33 2 14.6 2.53 15.54 3.46C16.47 4.4 17 5.67 17 7V11" stroke="#A0ABA8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckLockIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M9 11L12 14L22 4" stroke="#A0ABA8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M21 12V19C21 19.53 20.79 20.04 20.41 20.41C20.04 20.79 19.53 21 19 21H5C4.47 21 3.96 20.79 3.59 20.41C3.21 20.04 3 19.53 3 19V5C3 4.47 3.21 3.96 3.59 3.59C3.96 3.21 4.47 3 5 3H16" stroke="#A0ABA8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#434F4D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function SpinnerIcon({ size = 20, color = '#FFFFFF' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className="register-spinner">
      <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="3" strokeOpacity="0.3" />
      <path d="M22 12a10 10 0 0 0-10-10" stroke={color} strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

// Tooltip component for field explanations
function FieldTooltip({ content }) {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, arrowLeft: 0 });
  const triggerRef = useRef(null);

  const handleMouseEnter = () => {
    if (!triggerRef.current) return;
    
    const rect = triggerRef.current.getBoundingClientRect();
    const tooltipWidth = 360;
    const tooltipHeight = 100; // approximate height
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate horizontal position (centered on icon, but keep within viewport)
    let left = rect.left + rect.width / 2 - tooltipWidth / 2;
    
    // Ensure tooltip stays within viewport horizontally
    if (left < 10) {
      left = 10;
    } else if (left + tooltipWidth > viewportWidth - 10) {
      left = viewportWidth - tooltipWidth - 10;
    }

    // Calculate arrow position (relative to tooltip)
    const arrowLeft = rect.left + rect.width / 2 - left;

    // Calculate vertical position (prefer above, fallback to below)
    let top;
    let position;
    const gapAbove = 14;
    const gapBelow = 24;
    
    if (rect.top - tooltipHeight - gapAbove > 10) {
      // Position above with smaller gap for Typical Day
      top = rect.top - tooltipHeight - gapAbove;
      position = 'above';
    } else {
      // Position below with larger gap for Training Background
      top = rect.bottom + gapBelow;
      position = 'below';
    }

    setPosition({ top, left, arrowLeft, position });
    setIsVisible(true);
  };

  const handleMouseLeave = () => setIsVisible(false);

  return (
    <>
      <div className="register-field-tooltip-wrapper" ref={triggerRef}>
        <button
          type="button"
          className="register-tooltip-trigger"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={() => {
            if (isVisible) {
              setIsVisible(false);
            } else {
              handleMouseEnter();
            }
          }}
          aria-label="Show help"
        >
          <HelpCircleIcon size={16} color="#A0ABA8" />
        </button>
      </div>
      {isVisible &&
        ReactDOM.createPortal(
          <div
            className="register-tooltip-portal"
            style={{
              position: 'fixed',
              top: `${position.top}px`,
              left: `${position.left}px`,
              zIndex: 9999,
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div className="register-tooltip-content" dangerouslySetInnerHTML={{ __html: content }} />
            <div
              className="register-tooltip-arrow"
              data-position={position.position}
              style={{
                position: 'absolute',
                left: `${position.arrowLeft}px`,
                border: '6px solid transparent',
              }}
            />
          </div>,
          document.body
        )
      }
    </>
  );
}

function CustomSelect({ value, placeholder, options, onChange, disabled, className = '' }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const selectedOption = options.find((option) => String(option.value) === String(value));

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  return (
    <div className={`register-custom-select ${className}`} ref={rootRef}>
      <button
        type="button"
        className="register-date-select register-custom-select-button"
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={!selectedOption ? 'register-custom-select-placeholder' : ''}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <span className="register-custom-select-chevron">⌄</span>
      </button>

      {open && (
        <div className="register-custom-select-menu" role="listbox">
          {options.map((option) => {
            const isSelected = String(option.value) === String(value);

            return (
              <button
                type="button"
                key={option.value}
                className={`register-custom-select-option ${
                  isSelected ? 'register-custom-select-option--selected' : ''
                }`}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                role="option"
                aria-selected={isSelected}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── MAIN ───

const STEPS = ['Account', 'About You', 'Body & Sleep'];

function RegisterPage() {
  const navigate = useNavigate();

  const [step, setStep] = useState(0);

  // "resume" = user already has an auth account (either from a prior session
  // that killed the app mid-onboarding, or because ProtectedRoute / Landing
  // redirected them here). In that mode, we skip the Account step entirely
  // and only collect the missing profile fields.
  const [resumeMode, setResumeMode] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(true);

  // Step 1
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Step 2
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [gender, setGender] = useState('');

  // Legal modal state
  const [legalModalType, setLegalModalType] = useState(null); // 'terms' or 'privacy'

  // Step 3
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [sleepGoal, setSleepGoal] = useState(null);
  // Replaces the old activityLevel field — backend now splits it into two.
  const [typicalDay, setTypicalDay] = useState('');
  const [trainingBackground, setTrainingBackground] = useState(null);

  // Auth state earned at step 1 (kept in component state only — not password)
  const [auth, setAuth] = useState(null); // { accessToken, refreshToken, userId }

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);

  // Decide on mount what flow to run:
  //   - No auth stored       → normal new-user flow starting at Step 1 (Account)
  //   - Auth + profile done  → user doesn't belong here → dashboard
  //   - Auth + profile gaps  → resume: prefill what's there, jump to Step 2
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const existing = getAuth();
      if (!existing) {
        if (!cancelled) setBootstrapping(false);
        return;
      }
      const result = await fetchProfileAndCompletionStatus();
      if (cancelled) return;

      if (!result) {
        // Stored token was rejected — wipe and start fresh.
        clearAuth();
        setBootstrapping(false);
        return;
      }

      if (result.profileComplete) {
        // Profile already done — let ProtectedRoute decide whether they
        // need /ai-intro (first time) or /dashboard (intro seen).
        navigate('/dashboard', { replace: true });
        return;
      }

      // Resume path: carry tokens into component state and prefill any
      // fields the backend already has (lets the user re-enter and correct
      // whatever they may have partially typed before abandoning).
      setAuth({
        accessToken: existing.token,
        refreshToken: existing.refreshToken,
        userId: existing.userId,
      });

      const p = result.profile || {};
      const extras = loadProfileExtras(existing.userId);
      if (p.firstName) setFirstName(p.firstName);
      if (p.lastName) setLastName(p.lastName);

      // birthDate may come from backend (now that response includes it) or
      // from the localStorage extras stash. Either way, also unpack the
      // YYYY-MM-DD/YYYY/MM/DD string into Day/Month/Year so the three
      // <select> dropdowns reflect what we have on file. Without this, the
      // hidden combined value is correct but the visible UI shows blank
      // placeholders, leading to the bug we just hit (Oyku's profile
      // looking incomplete on web because the dropdowns weren't set).
      const dobValue = p.birthDate || extras.dateOfBirth;
      if (dobValue) {
        setDateOfBirth(dobValue);
        const m = /^(\d{4})[-\/](\d{1,2})[-\/](\d{1,2})$/.exec(String(dobValue));
        if (m) {
          setBirthYear(m[1]);
          setBirthMonth(String(parseInt(m[2], 10))); // strip any leading 0
          setBirthDay(String(parseInt(m[3], 10)));
        }
      }

      if (p.gender) setGender(p.gender);
      if (p.height != null) setHeight(String(p.height));
      if (p.weight != null) setWeight(String(p.weight));
      if (p.sleepGoal != null) setSleepGoal(parseSleepGoal(p.sleepGoal));
      if (p.typicalDay) setTypicalDay(p.typicalDay);
      if (typeof p.trainingBackground === 'number') setTrainingBackground(p.trainingBackground);

      setResumeMode(true);
      setStep(1); // skip Account step — we already have one
      setBootstrapping(false);
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  // Sync dateOfBirth from dropdowns
  useEffect(() => {
    if (birthYear && birthMonth && birthDay) {
      const month = birthMonth.padStart(2, '0');
      const day = birthDay.padStart(2, '0');
      setDateOfBirth(`${birthYear}-${month}-${day}`);
    } else {
      setDateOfBirth('');
    }
  }, [birthYear, birthMonth, birthDay]);

  // Parse existing dateOfBirth into dropdowns when resuming/prefilling.
  useEffect(() => {
    const { birthYear: y, birthMonth: m, birthDay: d } = splitDateOfBirth(dateOfBirth);
    if (y && m && d && (!birthYear || !birthMonth || !birthDay)) {
      setBirthYear(y);
      setBirthMonth(m);
      setBirthDay(d);
    }
  }, [dateOfBirth, birthYear, birthMonth, birthDay]);

  const validateStep = (s) => {
    if (s === 0) {
      if (!email.trim() || !password.trim() || !confirmPassword.trim()) {
        return 'Please fill in all fields.';
      }
      if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
        return 'Please provide a valid email address.';
      }
      if (confirmPassword !== password) {
        return 'Passwords do not match.';
      }
      if (!PASSWORD_REGEX.test(password)) {
        return 'Password must be 8–64 characters and include uppercase, lowercase, a number, and a special character (.@#$%^&+=*!).';
      }
      return null;
    }
    if (s === 1) {
      if (!firstName.trim()) return 'First name is required.';
      if (!lastName.trim()) return 'Last name is required.';
      const derivedAge = calculateAgeFromDateOfBirth(dateOfBirth);
      if (!dateOfBirth || derivedAge == null) {
        return 'Please select your date of birth.';
      }
      if (derivedAge < 13 || derivedAge > 95) {
        return 'Date of birth must result in an age between 13 and 95.';
      }
      if (!gender) return 'Please select your gender.';
      return null;
    }
    if (s === 2) {
      const parsedHeight = parseInt(height, 10);
      const parsedWeight = parseInt(weight, 10);
      // Ranges match the mobile onboarding (HEIGHT_MIN/MAX 90-240,
      // WEIGHT_MIN/MAX 15-300) so backend sees the same envelope from both
      // platforms.
      if (!parsedHeight || parsedHeight < 90 || parsedHeight > 240) {
        return 'Please enter a valid height (90–240 cm).';
      }
      if (!parsedWeight || parsedWeight < 15 || parsedWeight > 300) {
        return 'Please enter a valid weight (15–300 kg).';
      }
      if (sleepGoal == null) return 'Please select a sleep goal.';
      // typicalDay and trainingBackground are required on mobile as of the
      // latest build — mirroring that here so the two flows ask for the
      // same fields. Both still stay out of the backend's
      // `isProfileComplete` check (server-side rule unchanged), so front-end
      // validation is what enforces them.
      if (!typicalDay) return 'Please select your typical day.';
      if (trainingBackground == null) return 'Please select your training background.';
      return null;
    }
    return null;
  };

  // Step 1 → 2 transition: this is where registration + auto-login happen.
  // Email collisions surface RIGHT HERE instead of at the very end.
  const registerAndLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      const trimmedEmail = email.trim();

      const registerResp = await fetch(API.register(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmedEmail, password }),
      });

      if (!registerResp.ok) {
        const msg = await registerResp.text();
        if (registerResp.status === 400) {
          throw new Error(msg && msg.length < 200 ? msg : 'This email is already in use.');
        }
        throw new Error(msg || 'Registration failed. Please try again.');
      }

      const loginResp = await fetch(API.login(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmedEmail, password }),
      });

      if (!loginResp.ok) {
        throw new Error('Account created but automatic sign-in failed. Please sign in manually.');
      }

      const loginData = await loginResp.json();
      const { accessToken, refreshToken, userId } = loginData;
      if (!accessToken || !userId) {
        throw new Error('Sign-in response was incomplete. Please sign in manually.');
      }

      // Persist tokens via the shared helper. Password is never stored.
      persistAuth({ accessToken, refreshToken, userId });

      setAuth({ accessToken, refreshToken, userId });
      return true;
    } catch (err) {
      setError(err.message || 'Something went wrong.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Final submit: we already have auth from step 1; PUT the profile fields.
  // Idempotent — safe to retry on network failure.
  const saveProfile = async () => {
    if (!auth) {
      setError('Session expired. Please start over.');
      return false;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        age: calculateAgeFromDateOfBirth(dateOfBirth),
        birthDate: dateOfBirth,
        gender,
        height: parseInt(height, 10),
        weight: parseInt(weight, 10),
        sleepGoal,
        // Replaces the old activityLevel field — backend now splits it.
        typicalDay: typicalDay || undefined,
        trainingBackground: trainingBackground ?? undefined,
      };

      const resp = await fetch(API.updateUser(auth.userId), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const msg = await resp.text();
        throw new Error(msg || 'Could not save profile. Please try again.');
      }

      saveProfileExtras(auth.userId, {
        dateOfBirth,
        birthDay: birthDay.padStart(2, '0'),
        birthMonth: birthMonth.padStart(2, '0'),
        birthYear,
      });

      setSuccess('Account ready! Meet your AI companion...');
    setTimeout(() => navigate('/ai-intro'), 1200);
      return true;
    } catch (err) {
      setError(err.message || 'Something went wrong.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const goNext = async () => {
    if (loading) return; // double-click guard
    setError(null);
    const err = validateStep(step);
    if (err) {
      setError(err);
      return;
    }

    if (step === 0) {
      const ok = await registerAndLogin();
      if (ok) setStep(1);
    } else if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      await saveProfile();
    }
  };

  const goBack = () => {
    if (loading) return;
    setError(null);
    if (step === 0) {
      navigate('/');
      return;
    }
    if (step === 1 && resumeMode) {
      // User was pushed here mid-onboarding and wants out. The cleanest
      // "cancel" for them is to sign out — they can sign back in later and
      // will be brought right back here to finish.
      clearAuth();
      navigate('/');
      return;
    }
    if (step === 1) {
      // Fresh flow: account was created at step 0 so we can't edit email
      // anymore. Block the move and hint what to do.
      setError('Your account is created — you can only continue from here.');
      return;
    }
    setStep(step - 1);
  };

  const isLastStep = step === STEPS.length - 1;

  const birthDayOptions = Array.from({ length: 31 }, (_, i) => {
    const day = i + 1;
    return {
      label: String(day),
      value: String(day),
    };
  });

  const birthMonthOptions = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ].map((month, i) => ({
    label: month,
    value: String(i + 1).padStart(2, '0'),
  }));

  const birthYearOptions = (() => {
    const currentYear = new Date().getFullYear();
    const minYear = currentYear - 95;
    const maxYear = currentYear - 13;

    return Array.from({ length: maxYear - minYear + 1 }, (_, i) => {
      const year = maxYear - i;
      return {
        label: String(year),
        value: String(year),
      };
    });
  })();

  // UI-only view of the steps list. In resume mode we hide the "Account"
  // step (it's already done) and relabel "Step N of M" accordingly.
  const uiSteps = resumeMode ? STEPS.slice(1) : STEPS;
  const uiPosition = resumeMode ? step - 1 : step; // 0-indexed position in uiSteps
  const uiTotal = uiSteps.length;
  const currentLabel = STEPS[step] || '';

  if (bootstrapping) {
    return (
      <div className="register-page-wrapper">
        <div className="register-bg-glow register-bg-glow--top" />
        <div className="register-bg-glow register-bg-glow--bottom" />
        <div style={bootstrapSpinnerStyles.root}>
          <div style={bootstrapSpinnerStyles.spinner} />
        </div>
      </div>
    );
  }

  return (
    <div className="register-page-wrapper">
      <div className="register-bg-glow register-bg-glow--top" />
      <div className="register-bg-glow register-bg-glow--bottom" />

      <button
        type="button"
        className="register-back-btn"
        onClick={(e) => {
          e.stopPropagation();
          clearAuth();
          navigate('/');
        }}
      >
        <BackIcon />
        <span>Back to Landing</span>
      </button>

      <div className="register-card">
        <div className="register-header">
          <img 
            src={require('../assets/onboarding/938a8ebc-324d-4470-88d9-3ac7c40c90d6.png')} 
            alt="HRV-4" 
            className="register-logo"
          />
          <h1 className="register-title">
            {resumeMode ? 'Complete your profile' : 'Create account'}
          </h1>
          <div className="register-subtitle">
            {resumeMode ? (
              <>
                <span>Just a bit more info to get you set up — Step {uiPosition + 1} of {uiTotal}:</span>
                <span>{currentLabel}</span>
              </>
            ) : (
              <>
                <span>Step {uiPosition + 1} of {uiTotal}:</span>
                <span>{currentLabel}</span>
              </>
            )}
          </div>
        </div>

        <div className="register-progress">
          {uiSteps.map((_, i) => (
            <span
              key={i}
              className={`register-progress-dot ${i <= uiPosition ? 'register-progress-dot--active' : ''}`}
            />
          ))}
        </div>

        <div className="register-form" role="form">
          {step === 0 && (
            <>
              <div className="register-input-group">
                <label className="register-input-label">Email</label>
                <div className="register-input-wrap">
                  <span className="register-input-icon"><EmailIcon /></span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    autoComplete="email"
                    className="register-input"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="register-input-group">
                <label className="register-input-label">Password</label>
                <div className="register-input-wrap">
                  <span className="register-input-icon"><LockIcon /></span>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a password"
                    autoComplete="new-password"
                    className="register-input"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="register-input-group">
                <label className="register-input-label">Confirm Password</label>
                <div className="register-input-wrap">
                  <span className="register-input-icon"><CheckLockIcon /></span>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    autoComplete="new-password"
                    className="register-input"
                    disabled={loading}
                  />
                </div>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <div className="register-input-group">
                <label className="register-input-label">First Name</label>
                <div className="register-input-wrap">
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Jane"
                    autoComplete="given-name"
                    className="register-input"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="register-input-group">
                <label className="register-input-label">Last Name</label>
                <div className="register-input-wrap">
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Doe"
                    autoComplete="family-name"
                    className="register-input"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="register-input-group">
                <label className="register-input-label">Date of Birth</label>
                <div className="register-date-row">
                  <CustomSelect
                    value={birthDay}
                    placeholder="Day"
                    options={birthDayOptions}
                    onChange={setBirthDay}
                    disabled={loading}
                    className="register-custom-select--day"
                  />

                  <CustomSelect
                    value={birthMonth}
                    placeholder="Month"
                    options={birthMonthOptions}
                    onChange={setBirthMonth}
                    disabled={loading}
                    className="register-custom-select--month"
                  />

                  <CustomSelect
                    value={birthYear}
                    placeholder="Year"
                    options={birthYearOptions}
                    onChange={setBirthYear}
                    disabled={loading}
                    className="register-custom-select--year"
                  />
                </div>
              </div>

              <div className="register-input-group">
                <label className="register-input-label">Gender</label>
                <select
                  className="register-input register-select"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Select your gender</option>
                  {GENDER_OPTIONS.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="register-input-group">
                <label className="register-input-label">Height (cm)</label>
                <div className="register-number-input-wrapper">
                  <input
                    type="number"
                    inputMode="numeric"
                    value={height}
                    onChange={(e) => setHeight(e.target.value.replace(/[^0-9]/g, ''))}
                    placeholder="e.g. 175"
                    className="register-input"
                    disabled={loading}
                  />
                  <div className="register-number-spinners">
                    <button
                      type="button"
                      className="register-number-spinner-btn"
                      onClick={() => setHeight(String(Math.min(240, parseInt(height || 0) + 1)))}
                      disabled={loading}
                      aria-label="Increase height"
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M7 4L4 8H10L7 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="register-number-spinner-btn"
                      onClick={() => setHeight(String(Math.max(90, parseInt(height || 0) - 1)))}
                      disabled={loading}
                      aria-label="Decrease height"
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M7 10L4 6H10L7 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <div className="register-input-group">
                <label className="register-input-label">Weight (kg)</label>
                <div className="register-number-input-wrapper">
                  <input
                    type="number"
                    inputMode="numeric"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value.replace(/[^0-9]/g, ''))}
                    placeholder="e.g. 70"
                    className="register-input"
                    disabled={loading}
                  />
                  <div className="register-number-spinners">
                    <button
                      type="button"
                      className="register-number-spinner-btn"
                      onClick={() => setWeight(String(Math.min(300, parseInt(weight || 0) + 1)))}
                      disabled={loading}
                      aria-label="Increase weight"
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M7 4L4 8H10L7 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="register-number-spinner-btn"
                      onClick={() => setWeight(String(Math.max(15, parseInt(weight || 0) - 1)))}
                      disabled={loading}
                      aria-label="Decrease weight"
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M7 10L4 6H10L7 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <div className="register-input-group">
                <label className="register-input-label">Sleep Goal</label>
                <select
                  className="register-input register-select"
                  value={sleepGoal ?? ''}
                  onChange={(e) => setSleepGoal(parseSleepGoal(e.target.value))}
                  disabled={loading}
                >
                  <option value="">Select your sleep goal</option>
                  {SLEEP_GOAL_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div className="register-input-group">
                <div className="register-field-label-with-tooltip">
                  <label className="register-input-label">Typical Day</label>
                  <FieldTooltip content="Describes your daily activity level. Examples:<br/>• Mostly Sitting (office worker, desk job)<br/>• Mostly Standing (retail, teacher)<br/>• Mostly Moving (delivery driver, construction worker)" />
                </div>
                <select
                  className="register-input register-select"
                  value={typicalDay}
                  onChange={(e) => setTypicalDay(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Select your typical day</option>
                  {TYPICAL_DAY_OPTIONS.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>

              <div className="register-input-group">
                <div className="register-field-label-with-tooltip training-tooltip">
                  <label className="register-input-label">Training Background</label>
                  <FieldTooltip content="Ranges from:<br/>• Beginner (no regular exercise)<br/>• Intermediate (some regular exercise)<br/>• Long-term consistent training (e.g., athletes, regular gym-goers for years)" />
                </div>
                <select
                  className="register-input register-select"
                  value={trainingBackground == null ? '' : String(trainingBackground)}
                  onChange={(e) => {
                    const v = e.target.value;
                    // Empty → null (untouched). Otherwise coerce to Integer;
                    // backend schema is Integer, not string.
                    setTrainingBackground(v === '' ? null : Number(v));
                  }}
                  disabled={loading}
                >
                  <option value="">Select your training background</option>
                  {TRAINING_BACKGROUND_OPTIONS.map((option) => (
                    <option key={option.value} value={String(option.value)}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {error && <div className="register-error-box">{error}</div>}
          {success && <div className="register-success-box">{success}</div>}

          <div className="register-btn-row">
            <button
              type="button"
              className="register-step-back-btn"
              onClick={goBack}
              disabled={loading}
            >
              {step === 0
                ? 'Cancel'
                : resumeMode && step === 1
                ? 'Sign Out'
                : 'Back'}
            </button>
            <button
              type="button"
              className="register-primary-btn register-primary-btn--inline"
              onClick={goNext}
              disabled={loading}
            >
              {loading ? (
                <SpinnerIcon size={20} color="#FFFFFF" />
              ) : isLastStep ? (
                'Finish'
              ) : step === 0 ? (
                'Create Account'
              ) : (
                'Continue'
              )}
            </button>
          </div>
        </div>

        {/* Divider + Sign In link are only meaningful when the user doesn't
            have an account yet. Hide them in resume mode. */}
        {step === 0 && !resumeMode && (
          <>
            <div className="register-divider">
              <span className="register-divider-line" />
              <span className="register-divider-text">or</span>
              <span className="register-divider-line" />
            </div>

            <button
              type="button"
              className="register-secondary-btn"
              onClick={() => navigate('/')}
            >
              Already have an account? <strong>Sign In</strong>
            </button>
          </>
        )}

        <p className="register-footer-note">
          By creating an account, you agree to our<br />
          <button type="button" className="register-footer-link" onClick={() => setLegalModalType('terms')}>
            Terms of Service
          </button>
          {' '}and{' '}
          <button type="button" className="register-footer-link" onClick={() => setLegalModalType('privacy')}>
            Privacy Policy
          </button>
        </p>
      </div>

      {legalModalType && (
        <LegalModal type={legalModalType} onClose={() => setLegalModalType(null)} />
      )}
    </div>
  );
}

export default RegisterPage;

const bootstrapSpinnerStyles = {
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
  },
  spinner: {
    width: 40,
    height: 40,
    border: '3px solid rgba(255, 255, 255, 0.4)',
    borderTopColor: '#FFFFFF',
    borderRadius: '50%',
    animation: 'register-bootstrap-spin 0.9s linear infinite',
  },
};

if (typeof document !== 'undefined' && !document.getElementById('register-bootstrap-keyframes')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'register-bootstrap-keyframes';
  styleEl.textContent = `@keyframes register-bootstrap-spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(styleEl);
}